import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { blogPosts } from '../data/blog';

const SERVICES = [
  { icon:'🌐', title:'Web Development', desc:'Fast, scalable websites and web apps — from landing pages to full platforms. Built to convert.', tags:['React','Next.js','WordPress','Shopify'] },
  { icon:'🎨', title:'Web Design', desc:'Premium UI/UX design that reflects your brand and drives action. Every pixel has a purpose.', tags:['UI/UX','Figma','Branding','Prototyping'] },
  { icon:'📱', title:'App Development', desc:'Cross-platform mobile apps for iOS & Android — beautiful, fast, and built to scale.', tags:['React Native','Flutter','iOS','Android'] },
  { icon:'🔍', title:'SEO', desc:'Data-driven SEO that improves rankings and drives consistent organic traffic.', tags:['On-Page','Technical SEO','Local SEO','Analytics'] },
  { icon:'📣', title:'Digital Marketing', desc:'Full-funnel marketing across Google, Meta, social, and email — turning clicks into customers.', tags:['Google Ads','Meta Ads','Social Media','Email'] },
];

const MARQUEE_ITEMS = ['Web Development','Web Design','App Development','SEO','Digital Marketing','React','Next.js','WordPress','Shopify','React Native','Flutter'];

const TESTIMONIALS = [
  { quote:"Codeviora transformed our online presence completely. Their attention to detail and technical expertise is unmatched.", name:"Paul S", role:"Founder, UK" },
  { quote:"Our website traffic doubled after CodeViora's SEO work. They know exactly what they're doing — results speak for themselves.", name:"Yvvone V", role:"CEO, Bossing Up" },
  { quote:"The app they built for us has 50,000+ downloads. Clean code, beautiful design, and they were available whenever we needed them.", name:"Arjun Mehta", role:"Co-founder, INDIA" },
  { quote:"Best investment we made for our business. The website is getting compliments from every client we show it to.", name:"Geo", role:"Director, AUS" },
  { quote:"Their digital marketing campaigns brought us qualified leads we had never seen before. ROI was 6x within 3 months.", name:"Josh K", role:"MD, UK" },
];

const STATS = [
  { value:'100+', label:'Projects Completed' },
  { value:'25+', label:'Happy Clients' },
  { value:'10+', label:'Years Experience' },
  { value:'98%', label:'Client Retention' },
];

const BLOG_IMAGES = {
  'Web Development':'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600&q=80',
  'SEO':'https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?w=600&q=80',
  'App Development':'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&q=80',
  'Digital Marketing':'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=600&q=80',
  'Web Design':'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=600&q=80',
};

function ServiceCarousel() {
  const [active, setActive] = useState(0);
  const timerRef = useRef(null);
  const reset = () => { clearInterval(timerRef.current); timerRef.current = setInterval(() => setActive(a => (a+1)%SERVICES.length), 4000); };
  useEffect(() => { reset(); return () => clearInterval(timerRef.current); }, []);
  const go = i => { setActive(i); reset(); };
  const s = SERVICES[active];
  return (
    <div className="bg-black rounded-2xl p-8 border border-white/10 h-full">
      <div className="flex items-center gap-2 mb-8">
        {SERVICES.map((_,i) => <button key={i} onClick={() => go(i)} className={`h-1 rounded-full transition-all duration-300 ${i===active?'bg-white w-8':'bg-white/20 w-4'}`} aria-label={`Service ${i+1}`} />)}
      </div>
      <div key={active} className="animate-fade-in">
        <span className="text-4xl mb-5 block">{s.icon}</span>
        <h3 className="text-2xl text-white mb-3" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{s.title}</h3>
        <p className="text-white/50 text-sm leading-relaxed mb-6">{s.desc}</p>
        <div className="flex flex-wrap gap-2">
          {s.tags.map(t => <span key={t} className="text-xs bg-white/5 text-white/60 border border-white/10 px-3 py-1 rounded-full">{t}</span>)}
        </div>
      </div>
      <div className="mt-8 flex gap-2">
        <button onClick={() => go((active-1+SERVICES.length)%SERVICES.length)} className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-white/40 hover:border-white hover:text-white transition-colors">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <button onClick={() => go((active+1)%SERVICES.length)} className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-white/40 hover:border-white hover:text-white transition-colors">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
      </div>
    </div>
  );
}

function TestimonialCarousel() {
  const [active, setActive] = useState(0);
  useEffect(() => { const t = setInterval(() => setActive(a => (a+1)%TESTIMONIALS.length), 5000); return () => clearInterval(t); }, []);
  const t = TESTIMONIALS[active];
  return (
    <div>
      <div className="bg-black border border-white/10 rounded-2xl p-8 mb-5 relative overflow-hidden" style={{minHeight:200}}>
        <svg
          className="absolute top-5 left-7 opacity-20"
          width="48"
          height="36"
          viewBox="0 0 48 36"
          fill="white"
        >
          <path d="M0 36V22.5C0 15 3 9 9 4.5L13.5 0l4.5 3C13.5 6 11 10 10.5 15H18V36H0zm27 0V22.5C27 15 30 9 36 4.5L40.5 0 45 3C40.5 6 38 10 37.5 15H45V36H27z"/>
        </svg>
        <div key={active} className="animate-fade-in relative z-10">
          <p className="text-white/70 text-base leading-relaxed mb-6">"{t.quote}"</p>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border border-white/10 rounded-full bg-black flex items-center justify-center text-white text-xs font-black">
              {t.name.split(' ').map(n=>n[0]).join('')}
            </div>
            <div>
              <p className="font-bold text-white text-sm">{t.name}</p>
              <p className="text-white/40 text-xs">{t.role}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 justify-center">
        {TESTIMONIALS.map((_,i) => <button key={i} onClick={() => setActive(i)} className={`h-1 rounded-full transition-all duration-300 ${i===active?'bg-white w-6':'bg-white/15 w-3'}`} aria-label={`Review ${i+1}`} />)}
      </div>
    </div>
  );
}

export default function Home() {

const [lightbox, setLightbox] = useState({ open: false, index: 0 });

const PROJECTS_HOME = [
  { url:require('../images/pf1.webp'), label:'WebToolSpark', tag:'Web Development' },
  { url:require('../images/pf2.webp'), label:'LetFind', tag:'Education Website' },
  { url:require('../images/pf3.webp'), label:'Wildveitch', tag:'WordPress Development' },
  { url:require('../images/pf4.webp'), label:'Urbancrazy', tag:'WordPress Development' },
  { url:require('../images/pf5.webp'), label:'Diversiply', tag:'Open Cart' },
  { url:require('../images/pf6.webp'), label:'Larimarmagic', tag:'WordPress Development' },
];

const prevImg = () => setLightbox(l => ({ ...l, index: (l.index - 1 + PROJECTS_HOME.length) % PROJECTS_HOME.length }));
const nextImg = () => setLightbox(l => ({ ...l, index: (l.index + 1) % PROJECTS_HOME.length }));
const closeImg = () => setLightbox({ open: false, index: 0 });

  useScrollReveal();
  return (
    <>
      <SEO title="Web Development, Design & Digital Marketing" description="CodeViora is an IT services company offering web development, web design, app development, SEO, and digital marketing. We build digital products that drive real results." canonical="/" />

      {/* HERO — BLACK GRID */}
      <section className="bg-grid-black pt-24 pb-20 md:pt-36 md:pb-32 relative overflow-hidden">
        <div className="container-max relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="section-label-inv animate-fade-in">IT Services Company</span>
              <h1 className="text-5xl sm:text-6xl md:text-7xl text-white leading-[1.05] mt-3 animate-fade-up" style={{fontFamily:'Anton,sans-serif',fontWeight:500,letterSpacing:'0.02em'}}>
                WE BUILD<br/>DIGITAL<br/><span className="text-white/35">PRODUCTS.</span>
              </h1>
              <p className="text-white/50 text-lg leading-relaxed mt-6 max-w-lg animate-fade-up" style={{animationDelay:'0.1s'}}>
                Web development, design, apps, SEO & digital marketing — your end-to-end IT partner for measurable growth.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-10 animate-fade-up" style={{animationDelay:'0.2s'}}>
                <Link to="/services" className="btn-primary-inv">Explore Services <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg></Link>
                <Link to="/contact" className="btn-outline-inv">Get Free Quote</Link>
              </div>
              <div className="grid grid-cols-4 gap-4 mt-14 pt-8 border-t border-white/10 animate-fade-up" style={{animationDelay:'0.3s'}}>
                {STATS.map(({value,label}) => (
                  <div key={label}>
                    <p className="text-2xl text-white" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{value}</p>
                    <p className="text-xs text-white/35 mt-0.5 leading-tight">{label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="hidden lg:grid grid-cols-2 gap-4 animate-fade-in" style={{animationDelay:'0.25s'}}>
              <img src={require('../images/b1.webp')} alt="Web development" className="rounded-2xl h-52 w-full object-cover border border-white/10" loading="eager" />
              <img src={require('../images/b2.webp')} alt="App development" className="rounded-2xl h-52 w-full object-cover border border-white/10 mt-8" loading="eager" />
              <img src={require('../images/b3.webp')} alt="UI UX design" className="rounded-2xl h-40 w-full object-cover border border-white/10" loading="eager" />
              <img src={require('../images/b4.webp')} alt="SEO analytics" className="rounded-2xl h-40 w-full object-cover border border-white/10 mt-8" loading="eager" />            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-pure-black border-y border-white/10 py-4 overflow-hidden" aria-label="Services">
        <div className="marquee-track">
          {[...MARQUEE_ITEMS,...MARQUEE_ITEMS].map((item,i) => (
            <span key={i} className="flex items-center gap-4 px-6 text-white/35 font-semibold text-xs tracking-widest uppercase whitespace-nowrap">
              <span className="w-1.5 h-1.5 rounded-full bg-white/20 flex-shrink-0" aria-hidden="true"/>
              {item}
            </span>
          ))}
        </div>
      </section>

      {/* SERVICES — WHITE GRID */}
      <section className="bg-grid-white py-20 md:py-28">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="reveal">
              <span className="section-label">What We Do</span>
              <h2 className="section-title mt-1">IT SERVICES BUILT FOR GROWTH.</h2>
              <p className="section-sub">We cover the full digital stack — design, development, SEO, and marketing — so you don't need five different agencies.</p>
              <div className="flex flex-col gap-3 mt-8">
                {SERVICES.map(({icon,title}) => (
                  <div key={title} className="flex items-center gap-3 text-black/40 hover:text-black transition-colors">
                    <span>{icon}</span><span className="text-sm font-semibold">{title}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8"><Link to="/services" className="btn-primary">See All Services</Link></div>
            </div>
            <div className="reveal" style={{transitionDelay:'100ms'}}><ServiceCarousel /></div>
          </div>
        </div>
      </section>

      {/* WHY US — BLACK GRID */}
      <section className="bg-grid-black py-20 md:py-28">
        <div className="container-max">
          <div className="reveal text-center mb-14">
            <span className="section-label-inv">Why CodeViora</span>
            <h2 className="section-title-inv">RESULTS OVER PROMISES.</h2>
            <p className="section-sub-inv mx-auto">We don't just deliver work — we deliver outcomes you can measure.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {icon:'⚡',title:'Fast Delivery',desc:'Typical projects launch in 2–6 weeks without cutting corners.'},
              {icon:'💰',title:'Transparent Pricing',desc:'No hidden fees. Clear quotes before we start anything.'},
              {icon:'🎯',title:'Result Focused',desc:'We measure success by traffic, leads, and revenue — not hours.'},
              {icon:'🛡️',title:'Post-Launch Support',desc:'Bug fixes, updates, and improvements included after launch.'},
              {icon:'🤝',title:'Dedicated Team',desc:'A dedicated point of contact, not a ticket system.'},
              {icon:'📊',title:'Data Driven',desc:'Every decision backed by analytics. No guesswork.'},
            ].map(({icon,title,desc},i) => (
              <div key={title} className="reveal card-black p-7" style={{transitionDelay:`${i*60}ms`}}>
                <span className="text-3xl mb-4 block">{icon}</span>
                <h3 className="text-lg text-white mb-2" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{title}</h3>
                <p className="text-sm text-white/40 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

     
      {/* WORK — WHITE GRID */}
      <section className="bg-grid-white py-20 md:py-28">
  <div className="container-max">
    <div className="reveal text-center mb-14">
      <span className="section-label">Our Work</span>
      <h2 className="section-title">PROJECTS WE'RE PROUD OF.</h2>
    </div>

    {/* Desktop Grid */}
    <div className="hidden md:grid md:grid-cols-3 gap-6">
      {PROJECTS_HOME.map(({ url, label, tag }, i) => (
        <div
          key={label}
          className="reveal card-white group overflow-hidden cursor-pointer relative"
          style={{transitionDelay:`${i*60}ms`}}
          onClick={() => setLightbox({ open: true, index: i })}
        >
          <div className="h-48 overflow-hidden rounded-t-2xl">
            <img src={url} alt={label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
          </div>
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center rounded-t-2xl">
            <svg className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" width="36" height="36" viewBox="0 0 24 24" fill="none">
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="p-5 flex items-center justify-between">
            <div>
              <p className="text-black font-semibold text-sm">{label}</p>
              <p className="text-xs text-black/35 mt-0.5">{tag}</p>
            </div>
            <svg width="16" height="16" viewBox="0 0 18 18" fill="none" className="text-black/15 group-hover:text-black transition-colors">
              <path d="M4 14L14 4M14 4H7M14 4v7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      ))}
    </div>

    {/* Mobile Carousel */}
    <div className="md:hidden relative">
      <div className="flex gap-4 overflow-x-auto scrollbar-none snap-x snap-mandatory pb-4" style={{scrollbarWidth:'none'}}>
        {PROJECTS_HOME.map(({ url, label, tag }, i) => (
          <div
            key={label}
            className="flex-shrink-0 w-72 card-white group overflow-hidden snap-start cursor-pointer relative"
            onClick={() => setLightbox({ open: true, index: i })}
          >
            <div className="h-44 overflow-hidden rounded-t-2xl">
              <img src={url} alt={label} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="p-5 flex items-center justify-between">
              <div>
                <p className="text-black font-semibold text-sm">{label}</p>
                <p className="text-xs text-black/35 mt-0.5">{tag}</p>
              </div>
              <svg width="16" height="16" viewBox="0 0 18 18" fill="none" className="text-black/15">
                <path d="M4 14L14 4M14 4H7M14 4v7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        ))}
      </div>
      <p className="text-center text-xs text-black/30 mt-3">← Swipe to see more →</p>
    </div>
  </div>
</section>

{/* Lightbox */}
{lightbox.open && (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center"
    style={{background:'rgba(0,0,0,0.95)'}}
    onClick={closeImg}
  >
    <button onClick={closeImg} className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10" aria-label="Close">
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
    </button>

    <div className="absolute top-5 left-1/2 -translate-x-1/2 text-white/50 text-sm font-medium">
      {lightbox.index + 1} / {PROJECTS_HOME.length}
    </div>

    <button onClick={e => { e.stopPropagation(); prevImg(); }} className="absolute left-4 md:left-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10" aria-label="Previous">
      <svg width="20" height="20" viewBox="0 0 16 16" fill="none"><path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
    </button>

    <div className="max-w-4xl max-h-[80vh] mx-16 md:mx-24" onClick={e => e.stopPropagation()}>
      <img
        src={PROJECTS_HOME[lightbox.index].url}
        alt={PROJECTS_HOME[lightbox.index].label}
        className="max-w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl"
      />
      <p className="text-center text-white/50 text-sm mt-4">
        {PROJECTS_HOME[lightbox.index].label} — {PROJECTS_HOME[lightbox.index].tag}
      </p>
    </div>

    <button onClick={e => { e.stopPropagation(); nextImg(); }} className="absolute right-4 md:right-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10" aria-label="Next">
      <svg width="20" height="20" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
    </button>

    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
      {PROJECTS_HOME.map((_, i) => (
        <button
          key={i}
          onClick={e => { e.stopPropagation(); setLightbox(l => ({ ...l, index: i })); }}
          className={`h-1.5 rounded-full transition-all duration-300 ${i === lightbox.index ? 'bg-white w-6' : 'bg-white/30 w-3'}`}
          aria-label={`Go to ${i + 1}`}
        />
      ))}
    </div>
  </div>
)}

      {/* TESTIMONIALS — BLACK GRID */}
      <section className="bg-grid-black py-20 md:py-28">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="reveal">
              <span className="section-label-inv">Testimonials</span>
              <h2 className="section-title-inv">CLIENTS SPEAK FOR THEMSELVES.</h2>
              <p className="section-sub-inv">Don't take our word for it. Here's what our clients say.</p>
              <div className="flex items-center gap-8 mt-10">
                <div><p className="text-4xl text-white" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>25+</p><p className="text-white/30 text-xs mt-1">Happy clients</p></div>
                <div className="w-px h-10 bg-white/10" aria-hidden="true"/>
                <div><p className="text-4xl text-white" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>98%</p><p className="text-white/30 text-xs mt-1">Satisfaction</p></div>
                <div className="w-px h-10 bg-white/10" aria-hidden="true"/>
                <div>
                  <div className="flex gap-0.5 mb-1">{[...Array(5)].map((_,i)=><svg key={i} width="12" height="12" viewBox="0 0 14 14" fill="white"><path d="M7 1l1.5 3.5L12 5l-2.5 2.5.5 3.5L7 9.5 4 11l.5-3.5L2 5l3.5-.5L7 1z"/></svg>)}</div>
                  <p className="text-white/30 text-xs">5.0 rating</p>
                </div>
              </div>
            </div>
            <div className="reveal" style={{transitionDelay:'100ms'}}><TestimonialCarousel /></div>
          </div>
        </div>
      </section>

      {/* BLOG — WHITE GRID */}
      <section className="bg-grid-white py-20 md:py-28">
        <div className="container-max">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12 reveal">
            <div>
              <span className="section-label">Blog</span>
              <h2 className="section-title">INSIGHTS & UPDATES.</h2>
            </div>
            <Link to="/blog" className="btn-outline text-sm whitespace-nowrap">All Posts →</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {blogPosts.slice(0,3).map(({slug,title,excerpt,category,date,readTime},i) => (
              <Link key={slug} to={`/blog/${slug}`} className="reveal card-white group overflow-hidden flex flex-col" style={{transitionDelay:`${i*80}ms`}}>
                <div className="h-44 overflow-hidden rounded-t-2xl">
                  <img src={BLOG_IMAGES[category]||BLOG_IMAGES['Web Development']} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-semibold text-black bg-black/5 border border-black/10 px-2.5 py-1 rounded-full">{category}</span>
                    <span className="text-xs text-black/30">{readTime}</span>
                  </div>
                  <h3 className="font-bold text-black leading-snug mb-2 group-hover:text-black/50 transition-colors line-clamp-2" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{title}</h3>
                  <p className="text-sm text-black/50 leading-relaxed flex-1 line-clamp-2">{excerpt}</p>
                  <p className="text-xs text-black/25 mt-4">{date}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      
      {/* PRICING */}
      <section className="bg-grid-black py-20 md:py-28">
        <div className="container-max">
          <div className="reveal text-center mb-14">
            <span className="section-label-inv">Pricing</span>
            <h2 className="section-title-inv">TRANSPARENT PRICING.<br/>NO SURPRISES.</h2>
            <p className="section-sub-inv mx-auto">Simple, honest pricing based on your project scope. Pick what fits your budget — we'll make it work.</p>
          </div>

          {(() => {
            const plans = [
              {
            range: 'Starter',
            inr: 'Under ₹15K',
            usd: '$180',
            eur: '€160',
            desc: 'Perfect for small businesses, landing pages, and personal brands getting online.',
            features: ['Single page website', 'Mobile responsive', 'Basic SEO setup', 'Contact form', '1 revision round', '7-day delivery'],
            highlight: false,
          },
          {
            range: 'Growth',
            inr: '₹15K – ₹40K',
            usd: '$180 – $480',
            eur: '€160 – €440',
            desc: 'Ideal for growing businesses needing a full website, app, or marketing campaign.',
            features: ['Multi-page website', 'CMS integration', 'On-page SEO', 'Google Analytics', '3 revision rounds', '14-day delivery'],
            highlight: true,
          },
          {
            range: 'Professional',
            inr: '₹40K – ₹1L',
            usd: '$480 – $1.2K',
            eur: '€440 – €1.1K',
            desc: 'For businesses ready to scale with custom web apps, e-commerce, or full campaigns.',
            features: ['Custom web app', 'E-commerce ready', 'Advanced SEO', 'Ad campaigns setup', 'Unlimited revisions', '30-day delivery'],
            highlight: false,
          },
          {
            range: 'Enterprise',
            inr: '₹1L+',
            usd: '$1.2K+',
            eur: '€1.1K+',
            desc: 'Full-scale IT solutions — complex platforms to ongoing digital marketing retainers.',
            features: ['Full platform build', 'Mobile app included', 'SEO + Paid Ads', 'Dedicated manager', 'Priority support', 'Custom timeline'],
            highlight: false,
          },
                      ];

            const PlanCard = ({ range, inr, usd, eur, desc, features, highlight }, i) => (
              <div
                key={range}
                className={`flex-shrink-0 flex flex-col rounded-2xl border transition-all duration-300 relative overflow-hidden
                  md:flex-shrink md:w-auto w-72 snap-start
                  ${highlight ? 'bg-white border-white' : 'bg-white/5 border-white/10 hover:border-white/30'}`}
              >
                {highlight && <div className="absolute top-0 left-0 right-0 h-1 bg-black" />}
                {highlight && (
                  <div className="absolute top-3 right-3">
                    <span className="text-xs font-bold bg-black text-white px-3 py-1 rounded-full">Most Popular</span>
                  </div>
                )}

                <div className={`p-7 flex-1 flex flex-col ${highlight ? 'text-black' : 'text-white'}`}>
                  <p className={`text-xs font-bold tracking-widest uppercase mb-4 ${highlight ? 'text-black/40' : 'text-white/30'}`}>{range}</p>

                  <p className={`text-3xl leading-none mb-2 ${highlight ? 'text-black' : 'text-white'}`} style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{inr}</p>
                  <div className={`flex items-center gap-2 text-xs mb-6 ${highlight ? 'text-black/40' : 'text-white/30'}`}>
                    <span>{usd}</span><span>·</span><span>{eur}</span>
                  </div>

                  <p className={`text-sm leading-relaxed mb-7 ${highlight ? 'text-black/60' : 'text-white/40'}`}>{desc}</p>

                  <ul className="flex flex-col gap-3 flex-1">
                    {features.map(f => (
                      <li key={f} className="flex items-start gap-3 text-sm">
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${highlight ? 'bg-black text-white' : 'bg-white/10 text-white'}`}>
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M2 5l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                        <span className={highlight ? 'text-black/70' : 'text-white/50'}>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8">
                    <Link to="/contact"
                      className={`w-full flex items-center justify-center gap-2 font-semibold px-5 py-3 rounded-xl text-sm transition-all duration-200 ${
                        highlight
                          ? 'bg-black text-white hover:bg-black/80'
                          : 'bg-white/10 text-white border border-white/10 hover:bg-white hover:text-black'
                      }`}>
                      Get Started
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </Link>
                  </div>
                </div>
              </div>
            );

            return (
              <>
                {/* Desktop Grid */}
                <div className="hidden md:grid md:grid-cols-4 gap-5">
                  {plans.map((plan, i) => PlanCard(plan, i))}
                </div>

                {/* Mobile Carousel */}
                <div className="md:hidden">
                  <div className="flex gap-4 overflow-x-auto scrollbar-none snap-x snap-mandatory pb-4" style={{scrollbarWidth:'none'}}>
                    {plans.map((plan, i) => PlanCard(plan, i))}
                  </div>
                  <p className="text-center text-xs text-white/30 mt-3">← Swipe to see plans →</p>
                </div>
              </>
            );
          })()}

          {/* Note */}
          <p className="reveal text-center text-white/30 text-xs mt-10">
            * All prices are estimates. Final quote depends on project complexity. <Link to="/contact" className="text-white/60 hover:text-white underline underline-offset-2">Contact us</Link> for a free custom quote.
          </p>
        </div>
      </section>


     {/* CTA — WHITE */}
    <section className="bg-grid-white py-20 border-t border-black/10">
      <div className="container-max text-center reveal">
        <h2 className="text-4xl md:text-6xl text-black leading-tight mb-5" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>
          READY TO BUILD<br/>SOMETHING GREAT?
        </h2>
        <p className="text-black/50 text-lg mb-8 max-w-xl mx-auto">Tell us about your project. We'll respond within 24 hours with a free consultation.</p>
        <Link to="/contact" className="btn-primary">
          Start Your Project
          <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><path d="M3 9H15M11 5l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </Link>
      </div>
    </section>
    </>
  );
}
